// Connection Discovery — Questions, Scoring, and Archetype Definitions

export const ARCHETYPES = {
  intentional_relationship: {
    label: 'Intentional Relationship',
    narrative: "You seem to be looking for emotional consistency, meaningful connection, and long-term potential. Stability matters. So does clarity.",
    bullets: ['Emotional consistency', 'Clear expectations', 'Long-term compatibility']
  },
  intentional_dating: {
    label: 'Intentional Dating',
    narrative: "You seem open to meaningful connection, but without forcing rigid timelines or outcomes. Connection matters, but so does letting things develop naturally.",
    bullets: ['Meaningful connection', 'Healthy pacing', 'Flexibility without chaos']
  },
  exploration: {
    label: 'Exploration',
    narrative: "You seem to be in a season of curiosity, openness, and discovery. New experiences may matter more than certainty right now.",
    bullets: ['Openness', 'Discovery', 'Flexibility']
  },
  companionship: {
    label: 'Companionship',
    narrative: "You may be looking for connection, chemistry, and shared experience without needing everything neatly defined upfront.",
    bullets: ['Chemistry', 'Shared experiences', 'Connection without pressure']
  },
  freedom_flexibility: {
    label: 'Freedom + Flexibility',
    narrative: "Autonomy appears to matter a lot right now. You may be seeking connection that leaves room for independence, openness, or less traditional structure.",
    bullets: ['Independence', 'Openness', 'Flexible structure']
  },
  supplemental_connection: {
    label: 'Supplemental Connection',
    narrative: "You may already have meaningful connection in your life and be exploring something additive rather than replacement.",
    bullets: ['Additive connection', 'Openness', 'Non-traditional structure']
  },
  reset_rebuilding: {
    label: 'Reset / Rebuilding',
    narrative: "You may still be recalibrating, healing, or figuring out what feels right next. That\u2019s a valid place to be.",
    bullets: ['Reflection', 'Recovery', 'Rebuilding clarity']
  }
};

export const QUESTIONS = [
  {
    id: 'q1',
    type: 'choice',
    prompt: 'Which sounds closest to what you\u2019re looking for right now?',
    options: [
      { label: 'Committed long-term relationship', scores: { intentional_relationship: 4, intentional_dating: 1 } },
      { label: 'Meaningful dating without forcing a timeline', scores: { intentional_dating: 4, companionship: 1 } },
      { label: 'New experiences and exploration', scores: { exploration: 4, freedom_flexibility: 2 } },
      { label: 'Connection without needing to define everything', scores: { companionship: 3, exploration: 2 } },
      { label: 'Freedom and flexibility', scores: { freedom_flexibility: 4, exploration: 1 } },
      { label: 'Something additive to an existing connection', scores: { supplemental_connection: 5, freedom_flexibility: 1 } },
      { label: 'Honestly, I\u2019m still figuring that out', scores: { reset_rebuilding: 4, exploration: 2 } }
    ]
  },
  {
    id: 'q2',
    type: 'choice',
    prompt: 'What matters most to you right now?',
    options: [
      { label: 'Emotional consistency', scores: { intentional_relationship: 4 } },
      { label: 'Chemistry', scores: { companionship: 2, exploration: 2 } },
      { label: 'Discovery', scores: { exploration: 4 } },
      { label: 'Independence', scores: { freedom_flexibility: 4 } },
      { label: 'Shared experiences', scores: { companionship: 3 } },
      { label: 'Openness', scores: { exploration: 3, freedom_flexibility: 3 } },
      { label: 'Clarity', scores: { intentional_dating: 3, reset_rebuilding: 2 } }
    ]
  },
  {
    id: 'q3',
    type: 'choice',
    prompt: 'How do you feel about exclusivity?',
    options: [
      { label: 'Important', scores: { intentional_relationship: 4 } },
      { label: 'Preferred', scores: { intentional_dating: 3 } },
      { label: 'Depends', scores: { companionship: 2, exploration: 2 } },
      { label: 'Open to alternatives', scores: { freedom_flexibility: 4, supplemental_connection: 2 } },
      { label: 'Not important right now', scores: { exploration: 3, freedom_flexibility: 3 } }
    ]
  },
  {
    id: 'q4',
    type: 'choice',
    prompt: 'What sounds most exhausting right now?',
    options: [
      { label: 'Emotional uncertainty', scores: { intentional_relationship: 3 } },
      { label: 'Pressure to define things quickly', scores: { intentional_dating: 3, companionship: 2 } },
      { label: 'Predictability', scores: { exploration: 4 } },
      { label: 'Feeling restricted', scores: { freedom_flexibility: 4 } },
      { label: 'Starting over', scores: { reset_rebuilding: 4 } },
      { label: 'Constant dating effort', scores: { companionship: 3, intentional_dating: 2 } }
    ]
  },
  {
    id: 'q5',
    type: 'choice',
    prompt: 'If you met someone great tomorrow, what feels most true?',
    options: [
      { label: 'I\u2019d be fully open to building something', scores: { intentional_relationship: 4 } },
      { label: 'I\u2019d move slowly', scores: { intentional_dating: 4 } },
      { label: 'I\u2019d stay curious and see where it goes', scores: { exploration: 4 } },
      { label: 'Depends on the structure', scores: { freedom_flexibility: 3, supplemental_connection: 3 } },
      { label: 'I\u2019m not sure I\u2019m ready', scores: { reset_rebuilding: 5 } }
    ]
  },
  {
    id: 'q6',
    type: 'slider',
    prompt: 'How important is long-term potential right now?',
    min: 1,
    max: 5,
    labels: ['Not at all', 'Very important'],
    scoring: [
      { value: 1, scores: { exploration: 4 } },
      { value: 2, scores: { companionship: 3 } },
      { value: 3, scores: { intentional_dating: 2 } },
      { value: 4, scores: { intentional_relationship: 3 } },
      { value: 5, scores: { intentional_relationship: 5 } }
    ]
  },
  {
    id: 'q7',
    type: 'slider',
    prompt: 'How important is autonomy in your relationship structure?',
    min: 1,
    max: 5,
    labels: ['Not at all', 'Extremely important'],
    scoring: [
      { value: 1, scores: { intentional_relationship: 4 } },
      { value: 2, scores: { intentional_dating: 2 } },
      { value: 3, scores: { companionship: 2 } },
      { value: 4, scores: { freedom_flexibility: 4 } },
      { value: 5, scores: { freedom_flexibility: 5, supplemental_connection: 2 } }
    ]
  },
  {
    id: 'q8',
    type: 'choice',
    prompt: 'Which feels most true?',
    options: [
      { label: 'I\u2019m building from a clean slate', scores: { intentional_relationship: 2, intentional_dating: 2 } },
      { label: 'My life already has meaningful relationships in it', scores: { supplemental_connection: 5 } },
      { label: 'My situation is a little more complex than that', scores: { supplemental_connection: 3, freedom_flexibility: 2, reset_rebuilding: 2 } }
    ]
  },
  {
    id: 'q9',
    type: 'choice',
    prompt: 'Be honest: how clear do you actually feel right now?',
    options: [
      { label: 'Very clear', scores: { intentional_relationship: 2, intentional_dating: 2 } },
      { label: 'Mostly clear', scores: { intentional_dating: 2 } },
      { label: 'Somewhat clear', scores: { companionship: 2 } },
      { label: 'Pretty uncertain', scores: { reset_rebuilding: 4 } },
      { label: 'That\u2019s literally why I\u2019m here', scores: { reset_rebuilding: 5, exploration: 1 } }
    ]
  }
];

// Calculate final archetype from accumulated scores
export const calculateResult = (answers) => {
  const totals = {};
  Object.keys(ARCHETYPES).forEach(key => { totals[key] = 0; });

  answers.forEach((answer, i) => {
    const question = QUESTIONS[i];
    if (!question || answer === null || answer === undefined) return;

    if (question.type === 'choice') {
      const option = question.options[answer];
      if (option?.scores) {
        Object.entries(option.scores).forEach(([key, value]) => {
          totals[key] = (totals[key] || 0) + value;
        });
      }
    } else if (question.type === 'slider') {
      const match = question.scoring.find(s => s.value === answer);
      if (match?.scores) {
        Object.entries(match.scores).forEach(([key, value]) => {
          totals[key] = (totals[key] || 0) + value;
        });
      }
    }
  });

  const winner = Object.entries(totals).reduce((a, b) => b[1] > a[1] ? b : a);
  return {
    archetype: winner[0],
    scores: totals,
    result: ARCHETYPES[winner[0]]
  };
};
